import React from "react";
import { Link } from "react-router-dom";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { jwtDecode } from "jwt-decode";

const User = ({ playerProfileData, userId }) => {
  const accessToken = localStorage.getItem("accessToken");
  const isPublicProfileView = !accessToken && Boolean(userId);
  let tokenUserId = null;
  if (accessToken) {
    try {
      tokenUserId = jwtDecode(accessToken)?.userId || null;
    } catch (error) {
      tokenUserId = null;
    }
  }
  const profileId =
    userId || playerProfileData?.id || playerProfileData?.userId || null;
  const isOwnProfile =
    Boolean(tokenUserId && profileId) && String(tokenUserId) === String(profileId);

  const suppressSeasonTournamentUi =
    playerProfileData?.suppressSeasonTournamentUi === true &&
    !isPublicProfileView;

  const gender = playerProfileData?.gender || "";

  const locationLabel = [playerProfileData?.cityName, playerProfileData?.va]
    .filter(Boolean)
    .join(", ");

  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return isNaN(d.getTime()) ? "N/A" : d.toISOString().split("T")[0];
  };

  const extractMatchPreference = (data) => {
    if (!data) return "N/A";
    const match = data.match(/t\u0000(.+)/);
    let result = match ? match[1] : "N/A";
    return result.replace(/[^\x20-\x7E]/g, "");
  };

  // =========================
  // ✅ FINAL PERCENTAGE LOGIC
  // =========================

  const gamesPercentage = Number(playerProfileData?.gamesPercentage ?? 0);
  const winPercentage = Number(playerProfileData?.winPercentage ?? 0) * 100;

  // Priority: gamesPercentage > winPercentage
  const finalPercentage = gamesPercentage > 0 ? gamesPercentage : winPercentage;

  const roundedPercentage = Math.round((finalPercentage || 0) * 10) / 10;

  // Winning (>= 50%) => dark green used across this page; losing (< 50%) => red
  const ballColor = roundedPercentage >= 50 ? "#90BF33" : "#F70A0A";

  // =========================

  const profileWins =
    playerProfileData?.wins ??
    playerProfileData?.currentWins ??
    playerProfileData?.current_wins ??
    0;

  const profileLosses =
    playerProfileData?.losses ??
    playerProfileData?.currentLosses ??
    playerProfileData?.current_losses ??
    0;

  const resolvedOverallRecord =
    playerProfileData?.overallRecord ||
    playerProfileData?.overall_record ||
    `${Number(profileWins)}-${Number(profileLosses)}`;

  const resolvedTournamentRecord =
    playerProfileData?.tournamentRecord ||
    playerProfileData?.tournament_record ||
    `${Number(profileWins)}-${Number(profileLosses)}`;

  const [recordWins, recordLosses] = String(resolvedTournamentRecord || "0-0")
    .split("-")
    .map((n) => Number(n) || 0);

  const isZeroRecord = recordWins === 0 && recordLosses === 0;

  const [overallWins, overallLosses] = String(resolvedOverallRecord || "0-0")
    .split("-")
    .map((n) => Number(n) || 0);

  const isZeroOverall = overallWins === 0 && overallLosses === 0;

  return (
    <div className="col-sm-12 col-lg-4 mb-5">
      <div className="player-info members-listing-desc memberBox-bg w-100 h-100 flu-box shadow">
        {/* HEADER */}
        <div className="profile-caption heading-golfer largeYHeightHeader">
          <h4>
            {playerProfileData?.fullName || "Player"}
            {gender ? ` | ${gender}` : ""}
            {locationLabel && (
              <span style={{ marginLeft: "14px" }}>{locationLabel}</span>
            )}
          </h4>
          <img src="/images/guest-2.png" alt="member" />
        </div>

        <div className="clear"></div>

        {/* BASIC INFO */}
        <div className="subsection p1">
          <div className="flu-box">
            <p>
              Home Court :{" "}
              <span className="greentext">{playerProfileData.homeCourt}</span>
            </p>

            <div className="subsection p2">
              <p>
                Member Since :{" "}
                <span className="greentext">
                  {formatDate(playerProfileData.memberSince)}
                </span>
              </p>

              {(accessToken || isPublicProfileView) && (
                <p>
                  Rating :{" "}
                  <span className="greentext">
                    {playerProfileData.rating} {playerProfileData.ratingName}
                  </span>
                </p>
              )}

              <p>
                Tournament Entered :{" "}
                <span className="greentext">
                  {playerProfileData.tournamentEntered}
                </span>
              </p>
            </div>

            <div className="subsection p3">
              <p>
                Current Tournament Standings :{" "}
                <span className="greentext">
                  {suppressSeasonTournamentUi
                    ? ""
                    : playerProfileData.standings}
                </span>
              </p>

              <p>
                Current Tournament Points :{" "}
                <span className="greentext">
                  {suppressSeasonTournamentUi ? "" : playerProfileData.points}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* TOURNAMENT RECORD */}
        <div className="subsection">
          <div className="flu-box shadow">
            <p>
              Tournament Record :{" "}
              {suppressSeasonTournamentUi ? null : isZeroRecord ? (
                <span className="greentext">{resolvedTournamentRecord}</span>
              ) : (
                <Link
                  to="/carter/members/record"
                  state={{ memberId: userId, recordType: "tournament" }}
                  className="greentext"
                  style={{ textDecoration: "underline" }}
                >
                  {resolvedTournamentRecord}
                </Link>
              )}
            </p>

            {!suppressSeasonTournamentUi && (
              <div className="winloss-sec" style={{ marginTop: "6px" }}>
                {Array.from({ length: recordWins }).map((_, idx) => (
                  <i key={`win-dot-${idx}`} className="winBall" />
                ))}
                {Array.from({ length: recordLosses }).map((_, idx) => (
                  <i key={`loss-dot-${idx}`} className="lossBall" />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* OVERALL */}
        <div className="subsection">
          <div className="flu-box shadow">
            <p>
              Overall Record :{" "}
              {isZeroOverall ? (
                <span className="greentext">{resolvedOverallRecord}</span>
              ) : (
                <Link
                  to="/carter/members/record"
                  state={{ memberId: userId, recordType: "overall" }}
                  className="greentext"
                  style={{ textDecoration: "underline" }}
                >
                  {resolvedOverallRecord}
                </Link>
              )}
            </p>

            <p>
              Best Results :{" "}
              <span className="greentext">{playerProfileData.bestResults}</span>
            </p>

            <p>
              Plays :{" "}
              <span className="greentext">{playerProfileData.plays}</span>
            </p>

            <p>
              Match Preference :{" "}
              <span className="greentext">
                {extractMatchPreference(playerProfileData.matchPreference)}
              </span>
            </p>

            <p>
              Open To Challenges :{" "}
              <span className="greentext">
                {String(playerProfileData?.openToChallenges) === "1"
                  ? "Yes"
                  : "No"}
              </span>
            </p>
          </div>
        </div>

        {/* ✅ FINAL PERCENTAGE */}
        <div className="subsection mb-5">
          <div className="flu-box shadow d-flex align-items-center justify-content-between p-3">
            <p className="mb-0">
              Games Won Percentage :{" "}
              <span
                style={{
                  color: ballColor,
                  fontWeight: 700,
                }}
              >
                {roundedPercentage}%
              </span>
            </p>

            <div style={{ width: 50, height: 50 }}>
              <CircularProgressbar
                value={roundedPercentage}
                text={`${roundedPercentage}%`}
                styles={buildStyles({
                  textSize: "30px",
                  pathColor: ballColor,
                  textColor: ballColor,
                  trailColor: "#d6d6d6",
                })}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default User;
